"use client";

import { useAnalytics } from "@/lib/analytics/hooks";

/**
 * "Remove ads — Pro →": the one way out of an ad unit for an unpaid viewer
 * once `adsDismissible` is false (owner rule 2026-09-28: ads cannot be closed,
 * only Pro removes them). Shared by every affiliate unit so the copy, the
 * target and the telemetry cannot drift apart.
 *
 * `relative z-10` lifts it above the unit's stretched partner link (the CTA's
 * `after:absolute after:inset-0` overlay), so a tap here opens pricing, not the
 * partner — and there is no nested `<a>`.
 *
 * Fires `ad_remove_upsell_clicked` (the G5 "wants the ads gone" signal) and
 * the long-standing `ad_pro_upsell_clicked`, so the freemium board's
 * upsell-click count stays continuous across this copy change.
 *
 * A plain `<a>`, not `next/link`: this renders in the apex, content and app
 * zones, and `/pricing` is served by apex only — a client-side transition from
 * another zone's router would ask the wrong app for the page.
 */
export function RemoveAdsLink({
	locale,
	surface,
	provider,
	className,
}: {
	locale: "en" | "id";
	surface: string;
	provider?: string;
	className?: string;
}) {
	const { trackAdProUpsellClicked, trackAdRemoveUpsellClicked } =
		useAnalytics();
	return (
		<a
			href={`/${locale}/pricing?intent=ad_free`}
			onClick={() => {
				trackAdRemoveUpsellClicked({ surface, locale, provider });
				trackAdProUpsellClicked({ surface, locale, provider });
			}}
			className={[
				"relative z-10 shrink-0 whitespace-nowrap rounded-sm text-xs font-medium text-muted-foreground underline-offset-2 transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background motion-reduce:transition-none",
				className,
			]
				.filter(Boolean)
				.join(" ")}
		>
			{locale === "id" ? "Hapus iklan — Pro →" : "Remove ads — Pro →"}
		</a>
	);
}
